package dev.akshaychordiya.pages.articles

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initIntroducingOksseKotlinMultiplatformSseLibraryPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("articles/IntroducingOksseKotlinMultiplatformSseLibrary.md", FrontMatterElement.Builder { addScalar("title", "Introducing OkSSE — Kotlin Multiplatform SSE library"); addScalar("date", "2020-08-31"); addScalar("description", "At Clue, we always want to keep finding new solutions as challenges arise. Recently, we needed to build a dynamic UI driven via backend which motivated the entire team 💪🏻 and…"); addScalar("originalUrl", "https://medium.com/bleeding-edge/introducing-oksse-kotlin-multiplatform-sse-library-7e6e804c885b"); addScalar("originalName", "Medium"); addScalar("image", "/images/articles/introducing-oksse-kotlin-multiplatform-sse-library/1oc4-uguNPTzmtvkjb8zVlQ2x.webp"); }))
}

@Page("/articles/introducing-oksse-kotlin-multiplatform-sse-library")
@Layout(".components.layouts.ArticleLayout")
@Composable
fun IntroducingOksseKotlinMultiplatformSseLibraryPage() {
    org.jetbrains.compose.web.dom.H2(attrs = { id("introducing-oksse-kotlin-multiplatform-sse-library") }) {
        org.jetbrains.compose.web.dom.Text("Introducing OkSSE — Kotlin Multiplatform SSE library")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("/images/articles/introducing-oksse-kotlin-multiplatform-sse-library/1oc4-uguNPTzmtvkjb8zVlQ2x.webp", "")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("Sharing is caring — Illustration by ")
            com.varabyte.kobweb.silk.components.navigation.Link("https://www.instagram.com/martapucciofficial/?hl=en") {
                org.jetbrains.compose.web.dom.Text("Marta Pucci")
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("At Clue, we always want to keep finding new solutions as challenges arise. Recently, we needed to build a dynamic UI driven via backend which motivated the entire team 💪🏻 and eventually led to the development of OkSSE library.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("And today, I’m super excited 🎉 to share ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/biowink/oksse/") {
            org.jetbrains.compose.web.dom.Text("OkSSE")
        }
        org.jetbrains.compose.web.dom.Text(" — an open source ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://en.wikipedia.org/wiki/Server-sent_events") {
            org.jetbrains.compose.web.dom.Text("Server sent events")
        }
        org.jetbrains.compose.web.dom.Text(" client library made with Kotlin multiplatform and Coroutine for the tech community to use and build their amazing use-cases.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("OkSSE is a client for Server Sent events protocol written in Kotlin Multiplatform. The implementation is written according to ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://www.w3.org/TR/2015/REC-eventsource-20150203") {
            org.jetbrains.compose.web.dom.Text("W3C Recommendation 03 February 2015")
        }
        org.jetbrains.compose.web.dom.Text(" specification 📝")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("OkSSE is an SSE client that’s modern, efficient and provides an easy-to-use API.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The aim of this article is to:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("walk you through our journey 🛣 ️of why we set out to use SSE protocol,")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("explain why we decided to build our own library,")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("and share how you can use the library for your use-cases")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("backstory") }) {
        org.jetbrains.compose.web.dom.Text("Backstory")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We started with an idea 💡to have a ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://www.youtube.com/watch?v=_Dom_Fn87C0") {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("backend driven UI")
            }
            org.jetbrains.compose.web.dom.Text(" and build an infrastructure for it")
        }
        org.jetbrains.compose.web.dom.Text(". That’s where we decided to use SSE [Server Sent Events] protocol to talk with the mobile clients [Android & iOS] in order to continuously stream 🌊 the data from the backend to render the UI on clients.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Check out the talk below if you are interested in learning more about how we used SSE for Backend Driven UI:")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://www.youtube.com/embed/_Dom_Fn87C0?feature=oembed") {
            org.jetbrains.compose.web.dom.Text("Watch on YouTube")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-s-sse") }) {
        org.jetbrains.compose.web.dom.Text("What’s SSE?")
    }
    org.jetbrains.compose.web.dom.Blockquote {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Server")
            }
            org.jetbrains.compose.web.dom.Text("-")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("sent events")
            }
            org.jetbrains.compose.web.dom.Text(" (SSE) is a ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("server")
            }
            org.jetbrains.compose.web.dom.Text(" push technology enabling a browser to receive automatic updates from a ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("server")
            }
            org.jetbrains.compose.web.dom.Text(" via HTTP connection — ")
            com.varabyte.kobweb.silk.components.navigation.Link("https://en.wikipedia.org/wiki/Server-sent_events") {
                org.jetbrains.compose.web.dom.Text("Wikipedia")
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("In a nutshell 🥜, SSE is built on top of well known HTTP and establishes a ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("unidirectional")
        }
        org.jetbrains.compose.web.dom.Text(" connection from server → client. The server continuously pushes data downstream ⬇️, which clients can stream until the app or tab [for web] is closed.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("reinvent-the-wheel") }) {
        org.jetbrains.compose.web.dom.Text("Reinvent the wheel 🎡")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("With backend driven UI in mind, both of our client teams (i.e Android and iOS) started searching for the best way to implement SSE on their corresponding platforms. Naturally, we found plenty of other SSE implementations for mobile clients…")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("…But most of them needed to be modified to accommodate our needs, were not maintained, or did not fully meet the specifications.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("possible-options") }) {
        org.jetbrains.compose.web.dom.Text("Possible options")
    }
    org.jetbrains.compose.web.dom.Ol {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Take existing libraries ⇢ modify them as needed ⇢ maintain")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Write our own ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("generic")
            }
            org.jetbrains.compose.web.dom.Text(" version")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We carefully investigated both options, and within the Android team, we decided it made more sense to write our own version. Our iOS team, however, opted to modify an existing library to avoid extra work setting up Kotlin Native [more on this later].")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("let-s-make-a-multi-platform-wheel") }) {
        org.jetbrains.compose.web.dom.Text("Let’s make a multi-platform wheel")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("At this point we knew we wanted to write our own SSE implementation but then we thought — why not write it in Kotlin multi-platform to allow sharing business logic between Android, iOS, and even other platforms in the future?")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We are already doing this to share our ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("core algorithm")
        }
        org.jetbrains.compose.web.dom.Text(" between Android, iOS and the backend. We gave a talk about this at KotlinConf — ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://www.youtube.com/watch?v=DctKvZOU56I") {
            org.jetbrains.compose.web.dom.Text("Sharing is caring! from KotlinConf")
        }
        org.jetbrains.compose.web.dom.Text(" — in case you’re interested in learning more.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Since this option made the most sense for our whole team, we decided to go for it.")
        }
    }
    org.jetbrains.compose.web.dom.Blockquote {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("PS: If you are new to Kotlin multi-platform, take a look at the official docs:")
            org.jetbrains.compose.web.dom.Br()
            com.varabyte.kobweb.silk.components.navigation.Link("https://kotlinlang.org/docs/reference/multiplatform.html") {
                org.jetbrains.compose.web.dom.Text("https://kotlinlang.org/docs/reference/multiplatform.html")
            }
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("let-s-dive-into-the-details") }) {
        org.jetbrains.compose.web.dom.Text("Let’s dive into the details 🏊🏊‍♀️")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Kotlin multi-platform projects are structured as shown below:")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("/images/articles/introducing-oksse-kotlin-multiplatform-sse-library/1AIICKhHVbzNRheWhAfIjrg.webp", "")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("Kotlin multi-platform module structure")
        }
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Common")
            }
            org.jetbrains.compose.web.dom.Text(": It should contain the common code to be shared between all the platforms a.k.a platform independent.   In our case, it contains major business logic and rules for SSE written once")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("JVM")
            }
            org.jetbrains.compose.web.dom.Text(": It should contain implementation specific to JVM.   In our case, it contains OkHttp as our HTTP client to fuel ⛽️ the SSE in the common code")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Currently, implementation isn’t set up for platforms other than iOS since with our current setup, they need to use the Typescript API to consume shared code which affects performance [as compared to Kotlin].")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That being said, our iOS team is working to set up Kotlin/Native so that they can directly use Kotlin 😍")
    }
    org.jetbrains.compose.web.dom.Blockquote {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("Note: The diagram shows major platforms using JVM / JS / Native. That doesn’t mean you cannot use the library on any other JVM platform like Spring; the same applies to JS / Native.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-s-special-with-oksse") }) {
        org.jetbrains.compose.web.dom.Text("What’s special with OkSSE 🎁")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Surely there are lots of alternatives for SSE client; we created our library with the following goals:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Follow W3C specification 📑")
            }
            org.jetbrains.compose.web.dom.Text(" to build something generic and as per the standard which everyone can rely on")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Leverage Kotlin language features")
            }
            org.jetbrains.compose.web.dom.Text(" like sealed classes, lambda functions, extension function to provide a simple and easy API for developers")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Leverage Kotlin coroutines")
            }
            org.jetbrains.compose.web.dom.Text(" to make it super easy to write non-blocking code and provide easier concurrency management to users of the API")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Leverage Kotlin multiplatform")
            }
            org.jetbrains.compose.web.dom.Text(" to have a single business logic, which can be used by all platforms to have a single source of truth and minimize multiple implementation of the same thing while also avoiding different bugs 🐞 on different platforms")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Keep it lightweight")
            }
            org.jetbrains.compose.web.dom.Text(" — OkSSE has a very lightweight implementation. For Android, it has dex count of just 336 🦩")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("how-to-use-it") }) {
        org.jetbrains.compose.web.dom.Text("How to use it")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You just need a coroutine scope to connect to SSE and start streaming all the events from the server.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Let’s look at an usage example on JVM / Android:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("class SseSample {\n   \n   val client = OkHttpClient.Builder().build()\n   val requestBuilder = Request.Builder()                         \n    \n   fun main() = runBlocking {\n        withContext(Dispatchers.Default) {\n            val eventSource = connectToSse(\n                client,\n                requestBuilder,\n                random = Random(randomSeed),\n                logger = object : Logger {\n                    // Various callbacks for logging\n                }\n            )\n            for (state in eventSource) {\n                // Triggered each time there is a new SSE \n                // state from server\n                println(\">>>> state -> \$state\")\n            }\n        }\n    }\n}\n") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You can read the data from the event here 👇")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("for (state in eventSource) {\n    when (it) {\n        is EventSourceState.WithoutEvent -> null\n        is EventSourceState.WithEvent -> it.lastEvent.let **{ **event **->\n            **val data: String = event.data\n            // Parse the JSON string to your data models \n        **}\n    **}\n}\n") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/biowink/oksse/tree/master/sample-jvm") {
            org.jetbrains.compose.web.dom.Text("sample app")
        }
        org.jetbrains.compose.web.dom.Text(" provided in the repository is a great starting point in understanding how to use OkSSE library especially for JVM / Android.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("kicking-it-off") }) {
        org.jetbrains.compose.web.dom.Text("Kicking it off ⚽️")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We are currently using OkSSE in our Android app, and we are super excited to be sharing it with the tech community. We hope you give it a spin 💫 and let us know what you think.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Check out the sample and ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/biowink/oksse/") {
            org.jetbrains.compose.web.dom.Text("the GitHub repository")
        }
        org.jetbrains.compose.web.dom.Text(" to try it out! The code has a lot of comments and tests 💪🏻")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("OkSSE also comes with small things like logging: an exponential back-off retry mechanism to prevent constantly hitting your server when there is an error or no network — you can even provide your own HTTP client.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("We hope to add bindings for other platforms and are happy to receive contributions from the amazing tech community 😎")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Interested in projects like ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/biowink/oksse") {
            org.jetbrains.compose.web.dom.Text("OkSSE")
        }
        org.jetbrains.compose.web.dom.Text("? Check out Clue’s current engineering openings ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://helloclue.com/jobs") {
            org.jetbrains.compose.web.dom.Text("here")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://medium.com/bleeding-edge/introducing-oksse-kotlin-multiplatform-sse-library-7e6e804c885b") {
            org.jetbrains.compose.web.dom.Text("Introducing OkSSE — Kotlin Multiplatform SSE library")
        }
        org.jetbrains.compose.web.dom.Text(" was originally published in ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://medium.com/bleeding-edge") {
            org.jetbrains.compose.web.dom.Text("Bleeding Edge")
        }
        org.jetbrains.compose.web.dom.Text(" on Medium, where people are continuing the conversation by highlighting and responding to this story.")
    }
}
